import {
  type JsonValue,
  type Logger,
  type Provider,
  type ResolutionDetails,
  ProviderEvents,
  OpenFeatureEventEmitter,
  StandardResolutionReasons,
} from '@openfeature/web-sdk';

export interface YepNopeFlagConfig {
  [key: string]: boolean;
}

/**
 * A provider that resolves feature flags using a simple object of flags.
 * This can be useful for testing, development, and internal flag management
 * (e.g. within a codebase and not managed by an external feature flag system).
 *
 * However, it doesn't support features such as dynamic updates at run-time or
 * contextual flag evaluation.
 *
 * @see https://openfeature.dev/docs/reference/concepts/provider
 * @see https://github.com/open-feature/js-sdk
 */
export default class YepNopeProvider implements Provider {
  // Adds runtime validation that the provider is used with the expected SDK
  public readonly runsOn = 'client';
  public readonly events = new OpenFeatureEventEmitter();

  readonly metadata = {
    name: YepNopeProvider.name,
  } as const;

  // internal store of flags
  private _flags: YepNopeFlagConfig;

  // logger is the OpenFeature logger to use
  private _logger?: Logger;

  /**
   * Create a new provider with the given flags.
   *
   */
  constructor(flagConfiguration: YepNopeFlagConfig = {}, logger?: Logger) {
    this._flags = { ...flagConfiguration };
    this._logger = logger;
    this._logger?.debug('YepNopeProvider instantiated', this._flags);
  }

  /**
   * Overwrites the configured flags.
   * Mostly used to faciliate certain scenarios in test application suites.
   *
   * @see https://github.com/open-feature/js-sdk/blob/f6bc6951a32fbbed9b35c1bdfd023b02874b87a1/packages/web/src/provider/in-memory-provider/in-memory-provider.ts#L39
   */
  putConfiguration(flagConfiguration: YepNopeFlagConfig) {
    try {
      const flagsChanged = Object.entries({
        ...flagConfiguration,
        ...this._flags,
      }).map(([key]) => key);

      this._flags = { ...flagConfiguration };
      this.events.emit(ProviderEvents.ConfigurationChanged, { flagsChanged });
      this._logger?.debug('YepNopeProvider putConfiguration success');
    } catch (err) {
      this.events.emit(ProviderEvents.Error);
      throw err;
    }
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean
    /* context: EvaluationContext,
    logger: Logger */
  ): ResolutionDetails<boolean> {
    const value = this._flags[flagKey] ?? defaultValue;
    this._logger?.debug(
      'YepNopeProvider `resolveBooleanEvaluation`',
      flagKey,
      value
    );
    return { value, reason: StandardResolutionReasons.STATIC };
  }

  resolveStringEvaluation() /* flagKey: string,
    defaultValue: string,
    context: EvaluationContext,
    logger: Logger */
  : ResolutionDetails<string> {
    throw new Error(
      'YepNopeProvider `resolveStringEvaluation` not implemented'
    );
  }

  resolveNumberEvaluation() /* flagKey: string,
    defaultValue: number,
    context: EvaluationContext,
    logger: Logger */
  : ResolutionDetails<number> {
    throw new Error(
      'YepNopeProvider `resolveNumberEvaluation` not implemented'
    );
  }

  resolveObjectEvaluation<T extends JsonValue> /* flagKey: string,
    defaultValue: T,
    context: EvaluationContext,
    logger: Logger */(): ResolutionDetails<T> {
    throw new Error(
      'YepNopeProvider `resolveObjectEvaluation` not implemented'
    );
  }
}
